/**
 * ConsentFeed — App Posture Section 05 (§8.5) "Recent consents" feed.
 *
 * Each row reads as the mockup shows:
 *   {when} · {actor} consented to {appName} · {scopeSummary} · {tier-badge} · [{kindLabel}]
 *
 * Consenting-actor attribution comes from the existing
 * `consentActor()` helper in `src/lib/security-center/consent-actor.ts`,
 * which already handles the four ConsentKind branches. Empty state per
 * plan §8.5.4.
 */

import type { ConsentRecordRow } from './appPermissionsTypes'
import {
  consentActor,
  formatConsentTimestamp,
  type ConsentRecordRow as LocalConsentRecordRow,
} from '@/lib/security-center/consent-actor'

/* -------------------------------------------------------------------------- */
/* Visual tokens                                                              */
/* -------------------------------------------------------------------------- */

/** Severity-tinted text + dot color for the tier badge. Matches the editorial
 *  palette already used by `<PriorityQueue>`. */
const TIER_COLOR: Record<ConsentRecordRow['highestPermissionTier'], string> = {
  Critical: '#C84B4B',
  High: '#C9AD80',
  Medium: '#4F86A8',
  Low: '#4A9B6E',
}

/** Map ConsentKind to a short eyebrow label. */
function kindLabel(kind: ConsentRecordRow['consentKind']): string {
  switch (kind) {
    case 'UserDelegated':
      return 'User'
    case 'AdminDelegated':
    case 'AdminAppRole':
      return 'Admin'
    case 'AllPrincipals':
      return 'Org-wide'
  }
}

/** Take the first scope from a space-separated scope string and render an
 *  inline summary: "Mail.Read" / "Mail.Read (+2 more)". */
function scopeSummary(scope: string | null | undefined): {
  primary: string
  rest: number
} {
  if (!scope) return { primary: '—', rest: 0 }
  const tokens = scope
    .split(/\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
  if (tokens.length === 0) return { primary: '—', rest: 0 }
  return { primary: tokens[0], rest: tokens.length - 1 }
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

interface ConsentFeedProps {
  rows: ConsentRecordRow[] | undefined
  isLoading?: boolean
}

const EMPTY_STATE_COPY =
  'No new app consents in the last 30 days. Activity will surface here as it happens.'

export default function ConsentFeed({ rows, isLoading }: ConsentFeedProps) {
  if (isLoading) {
    return (
      <div
        className="
          rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#FFFFFF)]
          px-6 py-8 text-center text-[14px] italic
          text-[color:var(--color-mute,#6B7280)]
        "
      >
        Reading the latest consent activity…
      </div>
    )
  }

  if (!rows || rows.length === 0) {
    return (
      <div
        className="
          rounded-[4px] border border-dashed border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#FFFFFF)]
          px-6 py-8 text-center text-[14px] italic
          text-[color:var(--color-mute,#6B7280)]
        "
      >
        {EMPTY_STATE_COPY}
      </div>
    )
  }

  return (
    <ul className="overflow-hidden rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#FFFFFF)]">
      {rows.map((row, idx) => (
        <ConsentRow key={row.id} row={row} isFirst={idx === 0} />
      ))}
    </ul>
  )
}

function ConsentRow({
  row,
  isFirst,
}: {
  row: ConsentRecordRow
  isFirst: boolean
}) {
  // The local type in consent-actor.ts is identical to the one re-exported
  // from appPermissionsTypes.ts, but TypeScript treats them nominally per
  // module. Cast through the helper-local shape since the structures match.
  const actor = consentActor(row as unknown as LocalConsentRecordRow)
  const stamp = formatConsentTimestamp(row.grantedAt)
  const { primary, rest } = scopeSummary(row.scope)
  const tierColor = TIER_COLOR[row.highestPermissionTier] ?? TIER_COLOR.Medium
  const kind = kindLabel(row.consentKind)

  return (
    <li
      className={[
        'flex flex-wrap items-baseline gap-x-2 gap-y-1 px-4 py-3',
        isFirst ? '' : 'border-t border-[color:var(--color-line,#E6E8EC)]',
      ].join(' ')}
    >
      <span className="font-display tabular-nums text-[12px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-navy,#212938)]">
        {stamp.date}
      </span>
      <span className="font-display tabular-nums text-[11px] text-[color:var(--color-faint,#9CA3AF)]">
        {stamp.time}
      </span>

      <span className="text-[12px] text-[color:var(--color-mute,#6B7280)]">·</span>

      <span className="text-[14px] font-semibold text-[color:var(--color-navy,#212938)]">
        {actor}
      </span>
      <span className="text-[14px] text-[color:var(--color-mute,#6B7280)]">
        consented to
      </span>
      <span className="text-[14px] font-semibold text-[color:var(--color-navy,#212938)]">
        {row.appDisplayName}
      </span>

      <span className="text-[12px] text-[color:var(--color-mute,#6B7280)]">·</span>

      <span className="font-mono text-[12px] text-[color:var(--color-navy,#212938)]">
        {primary}
        {rest > 0 && (
          <span className="ml-1 text-[color:var(--color-faint,#9CA3AF)]">
            (+{rest} more)
          </span>
        )}
      </span>

      <span className="text-[12px] text-[color:var(--color-mute,#6B7280)]">·</span>

      <span
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em]"
        style={{ color: tierColor }}
      >
        <span
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{ background: tierColor }}
          aria-hidden
        />
        {row.highestPermissionTier}
      </span>

      <span className="text-[12px] text-[color:var(--color-mute,#6B7280)]">·</span>

      <span className="rounded-[2px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper-warm,#FBF8F2)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--color-mute,#6B7280)]">
        {kind}
      </span>
    </li>
  )
}
